import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Alert,
} from "@mui/material";
import { NucToast } from "src/reusable/components/Toast";
import {
  useGetFeatureSheet,
  useUpdateFeatureSheet,
  useReplaceFeatureSheet,
} from "./services/PartsAIFeatureSheetService";
import FeatureRowsEditor from "./components/FeatureRowsEditor";
import {
  createEmptyRow,
  featuresToRows,
  rowsToFeatures,
  type FeatureSheetRow,
} from "./config/featureRows";

interface PartsAIFeatureSheetProps {
  partsAIRunId: number | undefined;
}

const PartsAIFeatureSheet = ({ partsAIRunId }: PartsAIFeatureSheetProps) => {
  const [rows, setRows] = useState<FeatureSheetRow[]>([]);
  const [initialSnapshot, setInitialSnapshot] = useState<FeatureSheetRow[]>([]);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [showDeleteIcons, setShowDeleteIcons] = useState(false);
  const [highlightedRowIds, setHighlightedRowIds] = useState<Set<number>>(
    new Set(),
  );

  // fetch feature sheet from backend
  // (auto-refetch on tool completion + sidebar tab switch is handled by PartsAISidebar)
  const {
    data: featureSheetData,
    isLoading: isFetching,
    isError: isFetchError,
    refetch: refetchFeatureSheet,
  } = useGetFeatureSheet(partsAIRunId);

  // mutation to update single feature sheet row
  const {
    mutate: updateFeatureSheet,
    isLoading: isSaving,
    isError: isUpdateError,
  } = useUpdateFeatureSheet();

  // mutation to replace entire feature sheet
  const { mutate: replaceFeatureSheet, isLoading: isReordering } =
    useReplaceFeatureSheet();

  // initialize rows from fetched data
  useEffect(() => {
    let highlightTimer: ReturnType<typeof setTimeout>;
    if (featureSheetData?.features) {
      const newRows = featuresToRows(
        featureSheetData.features,
        featureSheetData.feature_order,
      );

      // detect which rows have changed by comparing feature/value pairs
      const changedRowIds = new Set<number>();
      const oldFeaturesMap = new Map(
        initialSnapshot.map((row) => [row.feature, row.value]),
      );

      newRows.forEach((newRow) => {
        const oldValue = oldFeaturesMap.get(newRow.feature);
        // highlight a row if it is a new feature OR the feature exists but value changed
        if (oldValue === undefined || oldValue !== newRow.value) {
          changedRowIds.add(newRow.__rowId);
        }
      });

      // highlight changed rows (if not the initial load)
      if (changedRowIds.size > 0 && initialSnapshot.length > 0) {
        setHighlightedRowIds(changedRowIds);

        // clear highlights after 2 seconds
        highlightTimer = setTimeout(() => {
          setHighlightedRowIds(new Set());
        }, 2000);
      }

      setRows(newRows);
      setInitialSnapshot(newRows);
      setShowDeleteIcons(false);
      setLastSavedAt(null);
    } else if (!isFetching && featureSheetData) {
      // empty feature sheet
      const emptyRows = [createEmptyRow()];
      setRows(emptyRows);
      setInitialSnapshot(emptyRows);
      setShowDeleteIcons(false);
      setLastSavedAt(null);
    }

    return () => {
      clearTimeout(highlightTimer);
    };
  }, [featureSheetData, isFetching]);

  // check if rows have changed from initial snapshot
  const rowsAreDirty = useMemo(() => {
    if (rows.length !== initialSnapshot.length) {
      return true;
    }

    // check for modified rows
    return rows.some((row, index) => {
      const initialRow = initialSnapshot[index];
      if (!initialRow) {
        return true;
      }

      // check for modified field names/values
      if (
        row.feature !== initialRow.feature ||
        row.value !== initialRow.value
      ) {
        return true;
      }

      // check for changed order
      if (row.__rowId !== initialRow.__rowId) {
        return true;
      }

      return false;
    });
  }, [rows, initialSnapshot]);

  const handleSave = () => {
    setShowDeleteIcons(false);
    if (!rowsAreDirty || isSaving || !partsAIRunId) {
      return;
    }

    const newFeatures = rowsToFeatures(rows);
    const oldFeatures = rowsToFeatures(initialSnapshot);

    // calculate deletions and changes
    const addedOrModifiedCount = Object.keys(newFeatures).filter(
      (key) => oldFeatures[key] !== newFeatures[key],
    ).length;
    const deletedCount = Object.keys(oldFeatures).filter(
      (key) => !(key in newFeatures),
    ).length;

    // check if row order has changed
    const orderChanged = rows.some((row, index) => {
      const initialRow = initialSnapshot[index];
      return !initialRow || row.__rowId !== initialRow.__rowId;
    });

    // replace the whole feature sheet if:
    // - rows were deleted
    // - multiple fields changed
    // - row order changed
    // - dirty with no field-level change (e.g. reorder only)
    const hasFieldChanges = addedOrModifiedCount + deletedCount > 0;
    const shouldReplace =
      deletedCount > 0 ||
      addedOrModifiedCount + deletedCount > 1 ||
      orderChanged ||
      (rowsAreDirty && !hasFieldChanges);

    if (shouldReplace) {
      // replace entire feature sheet
      replaceFeatureSheet(
        { runId: partsAIRunId, features: newFeatures },
        {
          onSuccess: () => {
            setInitialSnapshot(rows);
            setLastSavedAt(new Date().toLocaleTimeString());
            NucToast({
              message: "Feature sheet saved successfully.",
              type: "success",
            });
          },
        },
      );
    } else if (hasFieldChanges) {
      // single field addition
      const changedKey = Object.keys(newFeatures).find(
        (key) => oldFeatures[key] !== newFeatures[key],
      );

      if (changedKey) {
        updateFeatureSheet(
          {
            runId: partsAIRunId,
            fieldName: changedKey,
            fieldValue: newFeatures[changedKey],
          },
          {
            onSuccess: () => {
              setInitialSnapshot(rows);
              setLastSavedAt(new Date().toLocaleTimeString());
              NucToast({
                message: "Feature sheet saved successfully.",
                type: "success",
              });
            },
          },
        );
      }
    }
  };

  const errorMessage = isFetchError
    ? "Unable to load the feature sheet right now."
    : isUpdateError
      ? "Failed to save feature sheet. Please try again."
      : null;

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
      }}
    >
      {/* CSV download button is in the sidebar header actions */}

      {/* Error Alert */}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
          <Button
            size="small"
            onClick={() => refetchFeatureSheet()}
            disabled={isFetching}
            startIcon={
              isFetching ? <CircularProgress size={14} color="inherit" /> : null
            }
          >
            {isFetching ? "Retrying..." : "Retry"}
          </Button>
        </Alert>
      )}

      <FeatureRowsEditor
        rows={rows}
        onRowsChange={setRows}
        disabled={isFetching || isReordering}
        highlightedRowIds={highlightedRowIds}
        showDelete={showDeleteIcons}
        onShowDeleteChange={setShowDeleteIcons}
      />

      {/* Save action + status */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        sx={{ mt: 2 }}
      >
        <Button
          variant="contained"
          color="primary"
          size="small"
          disableElevation
          onClick={handleSave}
          data-attr="parts-ai:btn:save-feature-sheet"
          disabled={
            !rowsAreDirty ||
            isSaving ||
            isFetching ||
            isReordering ||
            !partsAIRunId
          }
          startIcon={
            isSaving || isReordering ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {isSaving || isReordering ? "Saving..." : "Save Changes"}
        </Button>

        {/* Status Text */}
        <Typography variant="body2" color="text.secondary">
          {isFetching
            ? "Loading feature sheet..."
            : lastSavedAt
              ? `Last saved at ${lastSavedAt}`
              : rowsAreDirty
                ? "Unsaved changes"
                : "All changes saved"}
        </Typography>
      </Stack>
    </Box>
  );
};

export default PartsAIFeatureSheet;
